import { useQuery } from '@tanstack/react-query';
import { LogIn, LogOut, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatMinutes } from '@hrms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/common/error-state';
import { useAuth } from '@/features/auth/use-auth';
import { employeesApi } from '@/features/employees/employees-api';
import { useClockIn, useClockOut, useToday } from './attendance-api';
import { PunchCaptureDialog, type PunchCaptureResult } from './punch-capture-dialog';

/** Manila wall-clock, formatted for display. */
function useManilaClock(): string {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Ticks on the minute rather than the second — a seconds display invites
    // people to watch it, and re-rendering once a second all day is waste.
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  return now.toLocaleTimeString('en-PH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Manila',
  });
}

function formatPunch(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-PH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Manila',
  });
}

export function ClockCard() {
  const { user } = useAuth();
  // An account with no employee record has no attendance to keep — an
  // administrator login, say. Asking the server for "today" only ever 400s
  // ("not linked to an employee record"), which rendered as a red error with a
  // "Try again" that could never succeed. Skip the doomed request and say so
  // plainly instead.
  const hasEmployee = Boolean(user?.employeeId);
  const { data: today, isLoading, isError, error, refetch } = useToday(hasEmployee);

  /*
   * The stamp burnt onto the photo should name the person in it. The session
   * carries only an email, so the name comes from the self record — cheap,
   * cached, and far less invasive than threading a name through token issuance
   * and rotation just to label an image.
   */
  const { data: me } = useQuery({
    queryKey: ['employees', 'me'],
    queryFn: () => employeesApi.me(),
    enabled: Boolean(user?.employeeId),
    staleTime: 30 * 60_000,
  });
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const time = useManilaClock();
  const [capturing, setCapturing] = useState(false);
  const [capturingOut, setCapturingOut] = useState(false);

  // No employee behind this login: a calm note, not an error and not a retry.
  if (!hasEmployee) {
    return (
      <Card>
        <CardContent className="px-6 py-8">
          <p className="text-sm text-muted-foreground">
            This account is not linked to an employee record, so there is no attendance to
            clock. Clocking in and out happens on employee accounts.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return <Skeleton className="h-36 rounded-xl" />;
  }

  /*
   * A failure used to fall into the skeleton above and stay there forever.
   *
   * `isLoading || !today` looked like it covered both, but a failed request is
   * not loading — it settles with `today` undefined — so the card the whole
   * company uses twice a day sat pretending to load, with no error, no retry
   * and no way to tell it apart from a slow connection. The one screen where
   * that matters most is the first one field staff open at the start of a
   * shift.
   *
   * Refusing to render the buttons is deliberate. Clocking in without knowing
   * whether a punch already exists is how somebody ends up with two.
   */
  if (isError || !today) {
    return (
      <ErrorState
        error={error}
        onRetry={() => void refetch()}
        title="Could not check your clock"
        className="px-6 py-8"
      />
    );
  }

  const isRestDay = today.scheduledTimeIn === null;
  const isDone = Boolean(today.timeIn && today.timeOut);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-3xl font-semibold tabular-nums tracking-tight">{time}</p>
          <p className="text-sm text-muted-foreground">
            {today.scheduleName ?? 'No schedule assigned'}
            {today.scheduledTimeIn && (
              <>
                {' · '}
                {today.scheduledTimeIn}–{today.scheduledTimeOut}
              </>
            )}
          </p>

          <div className="flex gap-4 pt-2 text-sm">
            <span>
              <span className="text-muted-foreground">In </span>
              <span className="font-medium tabular-nums">{formatPunch(today.timeIn)}</span>
            </span>
            <span>
              <span className="text-muted-foreground">Out </span>
              <span className="font-medium tabular-nums">{formatPunch(today.timeOut)}</span>
            </span>
            {today.workedMinutes > 0 && (
              <span>
                <span className="text-muted-foreground">Worked </span>
                <span className="font-medium">{formatMinutes(today.workedMinutes)}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
          {today.canClockIn && (
            <Button size="lg" onClick={() => setCapturing(true)} loading={clockIn.isPending}>
              <LogIn aria-hidden="true" />
              Clock in
            </Button>
          )}
          {today.canClockOut && (
            <Button
              size="lg"
              variant="outline"
              onClick={() => setCapturingOut(true)}
              loading={clockOut.isPending}
            >
              <LogOut aria-hidden="true" />
              Clock out
            </Button>
          )}
          {isDone && <p className="text-sm text-muted-foreground">Done for today</p>}
        </div>
      </CardContent>

      {isRestDay && !isDone && (
        <CardContent className="pt-0">
          {/* Clocking in is still allowed — rest-day work is normal for field
              crews and is paid at a premium — but it should be deliberate. */}
          <Alert variant="warning">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>
              Today is a rest day on your schedule. Hours worked will be recorded as rest-day
              overtime.
            </AlertDescription>
          </Alert>
        </CardContent>
      )}

      <PunchCaptureDialog
        open={capturing}
        onOpenChange={setCapturing}
        employeeLabel={me ? `${me.fullName} · ${me.employeeNo}` : (user?.email ?? '')}
        title="Photo to clock in"
        submitLabel="Clock in"
        submitting={clockIn.isPending}
        requiresLocation={today?.requiresLocation ?? true}
        onCapture={(capture: PunchCaptureResult) => {
          clockIn.mutate(capture, {
            // Closed only on success: a failed upload must leave the camera up
            // so the punch can be retried, rather than dropping the person back
            // to a card that still says they have not clocked in.
            onSuccess: () => setCapturing(false),
          });
        }}
      />

      {/*
        The same capture on the way out. The end of a shift is what decides the
        hours paid, and an arrival photo says nothing about where somebody was
        when they stopped.
      */}
      <PunchCaptureDialog
        open={capturingOut}
        onOpenChange={setCapturingOut}
        employeeLabel={me ? `${me.fullName} · ${me.employeeNo}` : (user?.email ?? '')}
        title="Photo to clock out"
        submitLabel="Clock out"
        submitting={clockOut.isPending}
        requiresLocation={today?.requiresLocation ?? true}
        onCapture={(capture: PunchCaptureResult) => {
          clockOut.mutate(capture, { onSuccess: () => setCapturingOut(false) });
        }}
      />
    </Card>
  );
}
